<script setup lang="ts">
import 'cropperjs/dist/cropper.css'

import VueCropper from 'vue-cropperjs'

import Modal from '~/components/shared/modal.vue'
import RatioContainer from '~/components/shared/ratioContainer.vue'

import { usePictureUploadUtils } from '~/composables/usePictureUploadUtils'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'
import { useProvideFileUploadFetch } from '~/composables/useProvideFileUploadFetch'

import { useUserStore } from '~/stores/user'

import { trackEvent } from '~/helpers/LegacyTrackEvent'

import { provideS3Upload } from '~/api-fileupload/PictureUpload'

import type {
  ExtendedEvent,
  PictureData,
} from '~/composables/usePictureUploadUtils'
import type { PictureSize, PictureTarget } from '~/helpers/images/picture_build'
import type { UserKind } from '~/types/UserKind'
import type { CropperData, VueCropperMethods } from 'vue-cropperjs'

/**
 * Loosely based on console.log returns.
 *
 * Don't super duper trust this and don't hesitate to update !
 * Https://www.youtube.com/watch?v=WJq4jWSQNd8.
 */
export type VueJsCropperEvent<T extends 'zoom' = 'zoom'> = {
  bubbles: boolean
  cancelBubble: boolean
  cancelable: boolean
  composed: boolean
  currentTarget: null // | ??
  defaultPrevented: boolean
  detail: {
    ratio: number
    oldRatio: number
    originalEvent: Event
  }
  eventPhase: number
  explicitOriginalTarget: HTMLElement
  isTrusted: boolean
  originalTarget: HTMLElement
  returnValue: boolean
  srcElement: HTMLElement
  target: HTMLElement
  timeStamp: number
  type: T | string
}

export type ImageCropperUploadResponse = {
  picture: string
}

type PictureResponse = { picture: string }

type Props = {
  aspectRatioX?: number
  aspectRatioY?: number
  target?: PictureTarget
  dragMode?: string
  containerStyle?: Record<string, string | number>
  modalTitle?: string
  preLoadedPicture?: PictureData['picture']
}

type Emits = {
  'file-change': [dataUrl?: string]
  ready: [payload: any]
  reset: []
  'update-picture-upload-progress': [percentageDone: number]
  zoom: [cropperEvent: VueJsCropperEvent<'zoom'>]
}

const props = withDefaults(defineProps<Props>(), {
  aspectRatioX: 1,
  aspectRatioY: 1,
  target: 'profile_picture',
  dragMode: 'move',
  containerStyle: () => ({
    width: '100%',
    height: '100%',
    maxHeight: '400px',
  }),
  modalTitle: '',
  preLoadedPicture: '',
})

const emit = defineEmits<Emits>()

const route = useRoute()
const { coreFetch } = useProvideCoreFetch()
const { fileUploadFetch } = useProvideFileUploadFetch()
const {
  picture,
  addPicture,
  preProcessPicture,
  reset: resetPictureUpload,
  uuid: cropperInputUuid,
} = usePictureUploadUtils()

const { KIND: USER_KIND, id: USER_ID } = storeToRefs(useUserStore())

const display = ref(false)
const loading = ref(false)
const cropper = ref<VueCropperMethods | null>(null)

const aspectRatio = computed(() => props.aspectRatioX / props.aspectRatioY)

defineExpose({
  upload,
  close,
  addPicture,
  getCroppedCanvas,
  getVueCropperData,
  fullReset,
  zoomTo,
  open,
})

watch(
  () => props.preLoadedPicture,
  (newVal) => {
    if (props.preLoadedPicture !== null && newVal !== null)
      picture.value = props.preLoadedPicture
  },
)

watch(
  () => picture.value,
  () => {
    if (picture.value !== null) open()
  },
)

watch(
  () => aspectRatio.value,
  () => {
    if (cropper.value) cropper.value.setAspectRatio(aspectRatio.value)
  },
)

async function handleFileInputChange(
  event: ExtendedEvent | Event,
): Promise<void> {
  const imageSrc = await preProcessPicture(event as ExtendedEvent)
  emit('file-change', imageSrc)
}

function handleClose() {
  fullReset()
  emit('reset')
}

function fullReset(): void {
  resetPictureUpload()
  close()
}

function zoomTo(value: number): void {
  cropper.value?.zoomTo(value)
}

function getVueCropperData(): CropperData | undefined {
  return cropper.value?.getData()
}

function getCroppedCanvas(): string | undefined {
  if (!cropper.value) return
  return cropper.value
    .getCroppedCanvas({
      fillColor: '#ffffff',
    })
    .toDataURL('image/jpeg', 1)
}

async function notifyCoreForResize({
  slug,
  kind,
  sizes,
  pictureBinary,
}: {
  slug: string
  kind: UserKind
  sizes: PictureSize<PictureTarget>[]
  pictureBinary: string
}): Promise<PictureResponse> {
  await coreFetch.$patch(`/${kind}/picture/${slug}/`, {
    sizes,
    target: props.target,
  })
  fullReset()
  loading.value = false
  return { picture: pictureBinary }
}

async function s3Upload({
  slug,
  kind,
  id: userId,
  token,
  spotifyPictureBinary,
}: {
  slug: string
  kind: UserKind
  id: number
  token: string
  spotifyPictureBinary?: string
}): Promise<PictureResponse> {
  const pictureBinary = spotifyPictureBinary || getCroppedCanvas()

  if (!pictureBinary) throw new Error('Could not retrieve picture binary')

  try {
    const uploadPictureOnS3 = provideS3Upload(fileUploadFetch)
    const sizes = await uploadPictureOnS3({
      slug,
      userId,
      userKind: kind,
      onUploadProgress: setOnUploadProgress,
      target: props.target,
      pictureBinary,
      token,
    })
    return await notifyCoreForResize({
      slug,
      kind,
      sizes,
      pictureBinary,
    })
  } catch (error) {
    loading.value = false
    throw error
  }
}

function setOnUploadProgress(progressEvent: { loaded: number; total: number }) {
  const percentCompleted = Math.round(
    (progressEvent.loaded * 100) / progressEvent.total,
  )
  emit('update-picture-upload-progress', percentCompleted)
}

async function upload({
  slug,
  kind,
  id,
  spotifyPictureBinary,
}: {
  slug: string
  kind?: UserKind
  id?: number
  spotifyPictureBinary?: string
}) {
  const userKind = kind ?? USER_KIND.value
  const userId = id ?? USER_ID.value

  if (!slug || !userKind)
    throw new Error(`No slug or missing kind ${slug} ${kind}`)

  let token

  try {
    token = await coreFetch.$get<string>('/auth/token/fileupload/')
  } catch (error) {
    throw new Error('Could not retrieve token for fileupload')
  }

  trackEvent(
    {
      category: 'Picture Cropper',
      action: 'Attempt picture upload',
    },
    route,
  )

  try {
    return await s3Upload({
      slug,
      token,
      spotifyPictureBinary,
      kind: userKind,
      id: userId,
    })
  } catch (error) {
    trackEvent(
      {
        category: 'Picture Cropper',
        action: 'Picture upload failed',
        error,
      },
      route,
    )
    throw error
  }
}

function close() {
  display.value = false
  trackEvent(
    {
      category: 'Picture Cropper',
      action: 'Close',
    },
    route,
  )
}

function open() {
  display.value = true
  trackEvent(
    {
      category: 'Picture Cropper',
      action: 'Open',
    },
    route,
  )
}
</script>

<template>
  <div class="imageCropperWrapper">
    <input
      :id="cropperInputUuid"
      type="file"
      name="fileInput"
      class="tw-hidden"
      @change="handleFileInputChange"
    />
    <Modal
      v-model="display"
      :keep-slot-mounted="picture !== null"
      :title="modalTitle"
      prevent-mask-click-close
      no-content-padding
      @close="handleClose"
    >
      <div>
        <div class="tw-px-2 tw-pt-2">
          <div class="sizeGuard tw-overflow-hidden" :style="containerStyle">
            <RatioContainer
              class="tw-relative"
              :style="containerStyle"
              :x-aspect="aspectRatioX"
              :y-aspect="aspectRatioY"
            >
              <slot name="title" />
              <div class="tw-relative tw-m-0 tw-h-full tw-w-full">
                <VueCropper
                  ref="cropper"
                  alt="Source Image"
                  :container-style="containerStyle"
                  :src="picture"
                  :drag-mode="dragMode"
                  :aspect-ratio="aspectRatio"
                  @zoom="emit('zoom', $event)"
                  @ready="emit('ready', $event)"
                />
              </div>
            </RatioContainer>
          </div>
        </div>
        <slot name="actions" />
      </div>
    </Modal>
  </div>
</template>
